import { useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";

import { campaignsApi } from "../../api/campaigns";
import { pipelinesApi } from "../../api/pipelines";
import { useConfirm } from "../confirm/context";
import { Pill } from "../Pill";
import { useToast } from "../toast/context";
import type {
  CampaignMembership,
  CampaignSuggestion,
  PipelineMembership,
  PipelineStage,
} from "../../types";

interface Props {
  contactId: string;
  contactName: string;
  memberships: PipelineMembership[];
  campaignMemberships: CampaignMembership[];
}

// Owns its pipelines/suggestions queries + membership mutations (same
// sanctioned exception as EnrollModal) — outcomes propagate by
// invalidating the parent page's ["contact", id] cache key.
export function PipelineMemberships({
  contactId,
  contactName,
  memberships,
  campaignMemberships,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [enrollSlug, setEnrollSlug] = useState("");

  const pipelinesQuery = useQuery({
    queryKey: ["pipelines", ""],
    queryFn: () => pipelinesApi.list({ limit: 200 }),
  });
  const pipelines = pipelinesQuery.data ?? [];
  const bySlug = new Map(pipelines.map((p) => [p.data.slug, p]));

  // Held = active or parked: a parked membership is still the contact's
  // pipeline (same stage, not being worked), so it stays in the main list.
  const held = memberships.filter((m) => m.status === "active" || m.status === "parked");
  const activeReal = held.filter((m) => !bySlug.get(m.slug)?.data.is_default && m.slug !== "default");
  const activeDefault = held.find((m) => m.slug === "default" && m.status === "active");
  const closed = memberships.filter((m) => m.status !== "active" && m.status !== "parked");

  const suggestionQueries = useQueries({
    queries: activeReal.map((m) => ({
      queryKey: ["pipeline-campaign-suggestions", m.slug],
      queryFn: () => pipelinesApi.campaignSuggestions(m.slug),
    })),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["contact", contactId] });

  const moveMutation = useMutation({
    mutationFn: (vars: {
      slug: string;
      to_stage?: string;
      close_status?: "goal_reached" | "dropped";
      park?: boolean;
      unpark?: boolean;
    }) =>
      pipelinesApi.moveMember(vars.slug, contactId, {
        to_stage: vars.to_stage,
        close_status: vars.close_status,
        park: vars.park || undefined,
        unpark: vars.unpark || undefined,
      }),
    onSuccess: invalidate,
    onError: (err) => toast.error(err),
  });

  const enrollMutation = useMutation({
    mutationFn: (slug: string) => pipelinesApi.enroll(slug, contactId),
    onSuccess: () => {
      setEnrollSlug("");
      void invalidate();
    },
    onError: (err) => toast.error(err),
  });

  const campaignChipMutation = useMutation({
    mutationFn: ({ slug, enrolled }: { slug: string; enrolled: boolean }) =>
      enrolled
        ? campaignsApi.unenroll(slug, contactId)
        : campaignsApi.enroll(slug, contactId),
    onSuccess: invalidate,
    onError: (err) => toast.error(err),
  });

  const enrolledCampaigns = new Set(
    campaignMemberships.filter((c) => c.status === "active").map((c) => c.slug),
  );

  const enrollable = pipelines.filter(
    (p) =>
      !p.data.is_default &&
      p.data.status === "active" &&
      !held.some((m) => m.slug === p.data.slug),
  );

  return (
    <div className="panel panel--flush">
      <div className="panel-header">
        <h2>Pipelines</h2>
        <div className="flex items-center gap-2">
          {enrollable.length > 0 ? (
            <>
              <select
                className="input input-sm w-[180px]"
                value={enrollSlug}
                onChange={(e) => setEnrollSlug(e.target.value)}
              >
                <option value="">Add to pipeline…</option>
                {enrollable.map((p) => (
                  <option key={p.data.slug} value={p.data.slug}>
                    {p.data.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-main btn-sm"
                disabled={!enrollSlug || enrollMutation.isPending}
                onClick={() => enrollMutation.mutate(enrollSlug)}
              >
                Add
              </button>
            </>
          ) : null}
        </div>
      </div>

      {activeDefault && activeReal.length === 0 ? (
        <div className="px-6 py-4 text-sm text-ink-low">
          Unsorted — in the default catch-all pipeline. Add the contact to a
          real pipeline to start tracking a goal.
        </div>
      ) : null}

      {activeReal.map((m, idx) => {
        const pipeline = bySlug.get(m.slug);
        const stages: PipelineStage[] = [...(pipeline?.data.stages ?? [])].sort(
          (a, b) => a.position - b.position,
        );
        const suggestions: CampaignSuggestion[] =
          suggestionQueries[idx]?.data ?? [];
        return (
          <div
            key={m.slug}
            className="px-6 py-4 border-t"
            style={{ borderColor: "var(--c-rule)" }}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 font-heading font-semibold text-ink">
                {pipeline?.data.name ?? m.slug}
                {m.status === "parked" ? <Pill value="parked" /> : null}
              </div>
              <div className="flex items-center gap-2">
                {m.status === "parked" ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title="Back to active in its stage"
                    disabled={moveMutation.isPending}
                    onClick={() => moveMutation.mutate({ slug: m.slug, unpark: true })}
                  >
                    Unpark
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title="Not being worked: same stage, off the board"
                    disabled={moveMutation.isPending}
                    onClick={() => moveMutation.mutate({ slug: m.slug, park: true })}
                  >
                    Park
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  title="Goal reached"
                  disabled={moveMutation.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Mark goal reached?",
                      body: `${contactName} leaves "${m.slug}" as goal reached.`,
                      confirmLabel: "Goal reached",
                    });
                    if (ok) moveMutation.mutate({ slug: m.slug, close_status: "goal_reached" });
                  }}
                >
                  ✓ Goal
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  title="Drop from pipeline (history is kept)"
                  disabled={moveMutation.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Drop ${contactName} from "${m.slug}"?`,
                      body: "History is kept.",
                      confirmLabel: "Drop",
                      tone: "danger",
                    });
                    if (ok) moveMutation.mutate({ slug: m.slug, close_status: "dropped" });
                  }}
                >
                  × Drop
                </button>
              </div>
            </div>

            {/* stage stepper */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {stages.map((s) => {
                const current = s.id === m.stage;
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={clsx("pill", current ? "pill--signal" : "pill--muted")}
                    style={{ cursor: current ? "default" : "pointer" }}
                    title={s.description || s.name}
                    disabled={current || moveMutation.isPending}
                    onClick={() => moveMutation.mutate({ slug: m.slug, to_stage: s.id })}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>

            {m.status === "parked" ? (
              <div className="text-xs text-ink-low mb-2">
                parked {m.parked_at?.slice(0, 10)}
                {m.parked_until ? ` · until ${m.parked_until}` : ""}
                {m.parked_reason ? ` · ${m.parked_reason}` : ""}
                {" · a reply or a new enrollment brings it back"}
              </div>
            ) : null}

            <div className="text-xs text-ink-low mb-2">
              {m.enrolled_at ? `since ${m.enrolled_at.slice(0, 10)}` : ""}
              {m.value_eur != null
                ? ` · €${(m.value_eur / 100).toLocaleString("de-DE")}`
                : ""}
              {m.probability != null ? ` · ${m.probability}%` : ""}
              {m.next_step ? ` · next: ${m.next_step}` : ""}
            </div>

            {/* campaign quick selection — campaigns other members of this
                pipeline are enrolled in */}
            {suggestions.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-ink-low mr-1">Campaigns:</span>
                {suggestions.map((s) => {
                  const enrolled = enrolledCampaigns.has(s.slug);
                  return (
                    <button
                      key={s.slug}
                      type="button"
                      className={clsx("pill", enrolled ? "pill--signal" : "pill--muted")}
                      title={
                        enrolled
                          ? `Remove from campaign "${s.slug}"`
                          : `${s.count} pipeline member(s) enrolled — click to enroll`
                      }
                      disabled={campaignChipMutation.isPending}
                      onClick={async () => {
                        if (
                          enrolled &&
                          !(await confirm({
                            title: `Remove ${contactName} from campaign "${s.slug}"?`,
                            confirmLabel: "Remove",
                            tone: "danger",
                          }))
                        ) {
                          return;
                        }
                        campaignChipMutation.mutate({ slug: s.slug, enrolled });
                      }}
                    >
                      {enrolled ? "✓ " : "+ "}
                      {s.name ?? s.slug}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}

      {closed.length > 0 ? (
        <details className="px-6 py-3 border-t" style={{ borderColor: "var(--c-rule)" }}>
          <summary className="text-xs text-ink-low cursor-pointer select-none">
            {closed.length} past membership{closed.length > 1 ? "s" : ""}
          </summary>
          <ul className="mt-2 space-y-1.5">
            {closed.map((m) => (
              <li key={m.slug} className="flex items-center gap-3 text-sm">
                <span className="text-ink-medium">
                  {bySlug.get(m.slug)?.data.name ?? m.slug}
                </span>
                <Pill value={m.status} />
                {m.closed_at ? (
                  <span className="text-xs text-ink-low ml-auto">
                    {m.closed_at.slice(0, 10)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
